

class StatCalc {

  /**
   * Generates a random number from a normal distribution using the Box-Muller transform.
   *
   * @param mean The mean (average) of the distribution
   * @param stdDev The standard deviation of the distribution
   * @returns A random number from the specified normal distribution
   */
  static randomNormal(mean: number = 0, stdDev: number = 1): number {
    // Box-Muller transform algorithm
    let u1 = Math.random();
    let u2 = Math.random();

    // Avoid u1 being exactly 0
    while (u1 === 0) {
      u1 = Math.random();
    }

    // Transform uniform random variables to standard normal random variable
    const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);

    // Scale and shift to get desired mean and standard deviation
    return z0 * stdDev + mean;
  }

  /**
   * Generates an array of random numbers from a normal distribution.
   *
   * @param count Number of values to generate
   * @param mean The mean (average) of the distribution
   * @param stdDev The standard deviation of the distribution
   * @returns An array of random numbers from the specified normal distribution
   */
  static generateNormalDistribution(count: number, mean: number = 0, stdDev: number = 1): number[] {
    const result: number[] = [];

    for (let i = 0; i < count; i++) {
      result.push(StatCalc.randomNormal(mean, stdDev));
    }

    return result;
  }

  /**
   * Calculates multiple percentiles of an array of numbers.
   * @param data - The array of numbers to calculate the percentiles from.
   * @param percentiles - Array of numbers between 0 and 1 representing the desired percentiles.
   * @returns Array of values at the specified percentiles. Returns NaN for any percentile if the input array is empty.
   * @throws Error if any percentile is not between 0 and 1.
   */
  static calculatePercentiles(data: number[], percentiles: number[]): number[] {
    // Validate percentiles
    for (const p of percentiles) {
      if (p < 0 || p > 1) {
        throw new Error('All percentiles must be between 0 and 1');
      }
    }

    // Handle empty data case
    if (data.length === 0) {
      return percentiles.map(() => NaN);
    }

    // Sort the data in ascending order (only once)
    const sortedData = [...data].sort((a, b) => a - b);

    // Calculate each percentile
    return percentiles.map(percentile => {
      // Handle edge cases
      if (percentile === 0) {
        return sortedData[0];
      }

      if (percentile === 1) {
        return sortedData[sortedData.length - 1];
      }

      // Calculate the index
      const index = percentile * (sortedData.length - 1);
      const lowerIndex = Math.floor(index);
      const upperIndex = Math.ceil(index);

      // If index is an integer, return the value at that index
      if (lowerIndex === upperIndex) {
        return sortedData[lowerIndex];
      }

      // Otherwise, interpolate between the two closest values
      const weight = index - lowerIndex;
      return sortedData[lowerIndex] * (1 - weight) + sortedData[upperIndex] * weight;
    });
  }
}

export default StatCalc